import {
  Repo,
  Api,
  GetParams,
  Filter,
  Sort,
  Http,
  Payload,
  RepoOrganizer,
  RequiredField,
} from 'common/types/admin-page.type';

export class AdminApi<T extends RequiredField> implements Api<T> {
  hasLoaded: boolean = false;

  constructor(private http: Http<T>, public repo: Repo<T>) {
    this.http = http;
    this.repo = repo;
  }

  async get(params: GetParams<T>) {
    if (!this.hasLoaded || params.refetch === true) {
      const response = await this.http.get();
      this.repo.items = response.data;
      this.hasLoaded = true;
    }

    const { pagination, filter, sort } = params;
    const { perPage, currPage } = pagination;
    const start = currPage - 1;

    let result = this.repo.filter(filter, this.repo.items);
    if (sort !== undefined && Object.keys(sort).length) {
      result = this.repo.sort(sort, result);
      result.sort((itemA, itemB) => {
        if (!itemA.enabled) return 1;
        if (!itemB.enabled) return -1;
        return 0;
      });
    } else if (typeof params.sortFallback === 'function') {
      result = params.sortFallback(result);
    }

    return {
      data: result.slice(start * perPage, start * perPage + perPage),
      pagination: {
        perPage,
        currPage,
        total: result.length,
      },
    };
  }

  async create(payload: Payload<T>) {
    const response = await this.http.create(payload);
    return { data: this.repo.add(response.data) };
  }

  async update(payload: Payload<T>) {
    const response = await this.http.update(payload);
    return { data: this.repo.update(response.data) };
  }

  async remove(id: T['id']) {
    await this.http.remove(id);
    this.repo.remove(id);
    return { data: id };
  }
}

export class AdminRepo<T extends RequiredField> implements Repo<T> {
  items: T[] = [];

  constructor(public organizer: RepoOrganizer<T>) {
    this.organizer = organizer;
  }

  add(item: T) {
    this.items = [...this.items, item];
    return this.find(item.id);
  }

  find(id: T['id']) {
    const match = this.items.find((item) => item.id === id);
    if (!match) {
      throw new Error(`Item not found: ${id}`);
    }

    return match;
  }

  update(item: T) {
    this.items = this.items.map((currItem) => (currItem.id === item.id ? item : currItem));
    return this.find(item.id);
  }

  remove(id: T['id']) {
    this.items = this.items.filter((item) => item.id !== id);
  }

  filter(filter: Filter<T>, items: T[]) {
    return this.organizer.filter(filter, items);
  }

  sort(sort: Sort<T>, items: T[]) {
    return this.organizer.sort(sort, items);
  }
}
